function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { PageHeader, Card, Empty, Pending, Search, Filters, Segmented } from "../../components/analytics-kit";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import ServiceFactory from "../../service";
import endpoints from "../../endpoints";
import account from "../../data/account";
import { AuthBase } from "../../../plunk/service/auth-base";
import { listAudiences, createAudience, toast as apiToast } from "../audiences/api";

/*
 * Data Enrichment (DATA_MARKETPLACE.md §3 "v1 = Data Enrichment, not
 * exchange"; mockup data-marketplace.html). Apps-registry card, feature
 * `dataEnrichment` (default OFF — unmetered vendor spend, deliberate
 * per-tenant enablement).
 *
 * LIVE backend (probed 2026-08-10, post VPC/JWKS fix):
 *   GET  /v1/person/lookup                    job list (bare array, unpaged)
 *   POST /v1/person/lookup                    multipart `file` (text/csv)
 *   GET  /v1/person/lookup/{id}               one job
 *   GET  /v1/person/lookup/found/{id}         found rows (bare array)
 *   /v1/company/lookup mirror.
 *   CSV columns (header-matched, all optional): person = FirstName,
 *   LastName, EmailAddress, PhoneNumber, StreetAddress, StreetAddress2,
 *   City, Region, PostalCode, Country; company = Company + address set.
 *
 * HONESTY RULES (spec gaps 1/2/4 are backend-proposed, not built):
 *  - No credit prices/quotes shown — nothing is metered yet. The
 *    pre-flight confirm shows the real row count + column mapping.
 *  - "Apply to CRM" renders DISABLED with an explanation — the
 *    create-contacts endpoint is a no-op 202 today (spec gap 2).
 *  - No download links — file urls are bare S3 keys, not presigned
 *    (spec gap 4); results render in-page from the found endpoint.
 *  - Job ids exceed 2^53 → reads go through ServiceFactory (JSONbig);
 *    ids handled as String() everywhere.
 */

const PERSON_COLS = ["FirstName", "LastName", "EmailAddress", "PhoneNumber", "StreetAddress", "StreetAddress2", "City", "Region", "PostalCode", "Country"];
const COMPANY_COLS = ["Company", "Website", "StreetAddress", "StreetAddress2", "City", "Region", "PostalCode", "Country"];

// Who to find at each company (company lookup only). Keys are the People Data
// Labs vocabulary the backend uses for both the PDL search and the title filter
// on Melissa contacts (POST /v1/company/lookup form fields roles / levels).
const ROLE_OPTIONS = [{
  key: "marketing",
  label: "Marketing"
}, {
  key: "sales",
  label: "Sales"
}, {
  key: "engineering",
  label: "IT / Technology"
}, {
  key: "operations",
  label: "Operations"
}, {
  key: "finance",
  label: "Finance"
}, {
  key: "customer_service",
  label: "Customer service"
}, {
  key: "human_resources",
  label: "HR"
}, {
  key: "design",
  label: "Design"
}];
const LEVEL_OPTIONS = [{
  key: "owner",
  label: "Owners & founders"
}, {
  key: "cxo",
  label: "C-suite & presidents"
}, {
  key: "vp",
  label: "VPs"
}, {
  key: "director",
  label: "Directors"
}, {
  key: "manager",
  label: "Managers"
}];
const PRODUCTS = {
  person: {
    title: "Person Enrichment",
    line: "Upload people with a name, email, phone or address (any of these). We fill the gaps with verified emails, phones and addresses.",
    cols: PERSON_COLS,
    api: "/person/lookup"
  },
  company: {
    title: "Company Contacts",
    line: "Upload companies with a website, or a name and address. Get the people at each company: names, titles, emails and phones. Narrow it to the roles you want to reach.",
    cols: COMPANY_COLS,
    api: "/company/lookup"
  }
};

// Minimal quoted-field-aware CSV parse — enough for pre-flight (headers +
// row count + preview). The server does the real parse.
export function parseCsv(text, maxRows) {
  const rows = [];
  let field = "",
    row = [],
    inQ = false;
  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    if (row.length > 1 || (row[0] || "").trim() !== "") rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQ = false;
      } else field += c;
    } else if (c === '"' && field === "") inQ = true;else if (c === ",") pushField();else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      pushField();
      pushRow();
      if (maxRows && rows.length > maxRows) return rows;
    } else field += c;
  }
  if (field !== "" || row.length) {
    pushField();
    pushRow();
  }
  return rows;
}
const norm = s => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Semicolon-joined multi-value blobs, sometimes wrapped in stray quotes.
export function splitBlob(v) {
  return String(v || "").replace(/^"\s*|\s*"$/g, "").split(";").map(x => x.trim()).filter(Boolean);
}
function svc(api) {
  const Service = ServiceFactory.getType(api, "audience");
  return new Service();
}
const isTerminal = s => ["completed", "error"].indexOf(String(s || "").toLowerCase()) > -1;
const isStalled = job => !isTerminal(job.status) && Date.now() - new Date(job.modifiedDate || job.createdDate).getTime() > 24 * 3600 * 1000;
function statusPill(job) {
  const s = String(job.status || "").toLowerCase();
  if (s === "completed") return {
    label: "Complete",
    cls: "ok"
  };
  if (s === "error") return {
    label: "Error",
    cls: "warn"
  };
  if (isStalled(job)) return {
    label: "Stalled",
    cls: "warn"
  };
  return {
    label: "Running",
    cls: "run"
  };
}
const SYSTEM_LISTS = ["all contacts", "customers"];
const firstOf = blob => splitBlob(blob)[0] || "";

// Found rows → the bulk-contact shape the audience import takes. People with
// neither an email nor a phone cannot become contacts, so they are left out.
export function toContacts(rows) {
  return (rows || []).map(r => {
    const c = {
      firstName: r.firstName || "",
      lastName: r.lastName || "",
      emailAddress: firstOf(r.emailAddress),
      phoneNumber: firstOf(r.phoneNumber).replace(/[^\d+]/g, ""),
      streetAddress: r.streetAddress || "",
      streetAddress2: r.streetAddress2 || "",
      locality: r.locality || "",
      region: r.region || "",
      postalCode: r.postalCode || "",
      country: r.country || ""
    };
    return c.emailAddress || c.phoneNumber ? c : null;
  }).filter(Boolean);
}

/**
 * Add selected found rows to an audience list: an existing static list, or a
 * new one created on the spot. POST /v1/company/lookup/found/add-to-audience/{jobId}
 * creates the people as contacts (company + title on the experience table),
 * matches existing contacts by email or phone, and adds them to the list.
 */
class AddToListDialog extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "run", async () => {
      const {
        mode,
        listId,
        newName,
        lists
      } = this.state;
      const rows = this.props.rows || [];
      const usable = toContacts(rows).length;
      if (!usable) {
        this.setState({
          err: "None of the selected people have an email or phone, so there is nothing to add."
        });
        return;
      }
      const body = {
        foundIds: rows.map(r => String(r.id))
      };
      if (mode === "new") {
        const name = newName.trim();
        if (!name) {
          this.setState({
            err: "Give the new list a name."
          });
          return;
        }
        body.name = name;
      } else {
        const list = (lists || []).find(a => String(a.id) === String(listId));
        if (!list) {
          this.setState({
            err: "Pick a list."
          });
          return;
        }
        body.audienceId = String(list.id);
      }
      this.setState({
        busy: true,
        err: ""
      });
      try {
        // Server-side: found rows → contacts (person, email/phone, address, company +
        // title on the experience table, matched to existing contacts by email or
        // phone) → list membership. Ids travel as strings (they exceed 2^53).
        const base = endpoints.audience && endpoints.audience.root || "";
        const res = await fetch(`${base}/company/lookup/found/add-to-audience/${encodeURIComponent(String(this.props.jobId))}`, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + (AuthBase.idToken || AuthBase.accessToken || ""),
            "x-app-id": (account.get() || {}).tenantId || "",
            "Content-Type": "application/json"
          },
          body: JSON.stringify(body)
        });
        const text = await res.text();
        let result = null;
        try {
          result = JSON.parse(text);
        } catch (e) {
          result = null;
        }
        if (!res.ok) throw {
          message: result && result.error || `Could not add them (HTTP ${res.status}).`
        };
        const added = Number(result && result.added) || 0;
        const created = Number(result && result.contactsCreated) || 0;
        const matched = Number(result && result.contactsMatched) || 0;
        const name = result && result.name || newName || "the list";
        apiToast(`${added.toLocaleString()} added to “${name}” (${created.toLocaleString()} new ${created === 1 ? "contact" : "contacts"}, ${matched.toLocaleString()} already on file).`);
        if (this._alive) this.setState({
          busy: false
        });
        this.props.onDone(result, added);
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          err: e && e.message || "Could not add them. Try again."
        });
      }
    });
    this.state = {
      lists: null,
      mode: "existing",
      listId: "",
      newName: "",
      busy: false,
      err: ""
    };
    this._alive = true;
  }
  componentDidMount() {
    listAudiences().then(r => {
      const all = Array.isArray(r) ? r : r && r.items || [];
      const lists = all.filter(a => a.kind !== "dynamic" && a.kind !== "system" && a.kind !== "mirror" && SYSTEM_LISTS.indexOf(String(a.name || "").toLowerCase()) < 0);
      if (this._alive) this.setState({
        lists,
        mode: lists.length ? "existing" : "new",
        listId: lists.length ? String(lists[0].id) : ""
      });
    }).catch(() => {
      if (this._alive) this.setState({
        lists: [],
        mode: "new"
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    const {
      lists,
      mode,
      listId,
      newName,
      busy,
      err
    } = this.state;
    const rows = this.props.rows || [];
    const usable = toContacts(rows).length;
    const skipped = rows.length - usable;
    return /*#__PURE__*/React.createElement("div", {
      className: "de-modal-backdrop",
      onClick: () => !busy && this.props.onClose()
    }, /*#__PURE__*/React.createElement("div", {
      className: "de-modal",
      onClick: e => e.stopPropagation(),
      role: "dialog",
      "aria-label": "Add to a list"
    }, /*#__PURE__*/React.createElement("h3", {
      style: {
        margin: "0 0 6px"
      }
    }, "Add ", usable.toLocaleString(), " ", usable === 1 ? "person" : "people", " to a list"), skipped > 0 && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px"
      }
    }, skipped.toLocaleString(), " of the selected rows have no email or phone and will be left out."), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px"
      }
    }, "They become contacts in your workspace, with their company and title, and members of the list. People already on file are matched by email or phone."), lists === null ? /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Loading your lists\u2026") : /*#__PURE__*/React.createElement("div", {
      className: "de-list-pick"
    }, lists.length > 0 && /*#__PURE__*/React.createElement("label", {
      className: "de-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "de-list-mode",
      checked: mode === "existing",
      onChange: () => this.setState({
        mode: "existing"
      })
    }), /*#__PURE__*/React.createElement("span", null, "An existing list")), lists.length > 0 && mode === "existing" && /*#__PURE__*/React.createElement("select", {
      className: "de-select",
      value: listId,
      onChange: e => this.setState({
        listId: e.target.value
      }),
      disabled: busy
    }, lists.map(a => /*#__PURE__*/React.createElement("option", {
      key: String(a.id),
      value: String(a.id)
    }, a.name, a.total != null ? ` (${Number(a.total).toLocaleString()})` : ""))), /*#__PURE__*/React.createElement("label", {
      className: "de-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "de-list-mode",
      checked: mode === "new",
      onChange: () => this.setState({
        mode: "new"
      })
    }), /*#__PURE__*/React.createElement("span", null, "A new list")), mode === "new" && /*#__PURE__*/React.createElement("input", {
      className: "de-input",
      type: "text",
      placeholder: "List name",
      value: newName,
      disabled: busy,
      autoFocus: true,
      onChange: e => this.setState({
        newName: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.run();
      }
    })), err ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        color: "#dc2626",
        margin: "8px 0 0"
      }
    }, err) : null, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "8px",
        justifyContent: "flex-end",
        marginTop: "14px"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      disabled: busy,
      onClick: this.props.onClose
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy || lists === null || !usable,
      onClick: this.run
    }, busy ? "Adding…" : mode === "new" ? "Create list and add" : "Add to list"))));
  }
}
class MainComponent extends React.Component {
  constructor(props) {
    super(props);
    // ── upload flow ──
    _defineProperty(this, "pickFile", type => () => {
      this.pendingType = type;
      if (this.fileRef.current) {
        this.fileRef.current.value = "";
        this.fileRef.current.click();
      }
    });
    _defineProperty(this, "onFile", async e => {
      const file = e.target.files && e.target.files[0];
      const type = this.pendingType;
      if (!file || !type) return;
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length < 2) {
        if (this.toast) this.toast.show("That file has no data rows. It needs a header row plus at least one row of data.");
        return;
      }
      const headers = rows[0];
      const known = PRODUCTS[type].cols;
      const matched = headers.filter(h => known.some(k => norm(k) === norm(h)));
      const ignored = headers.filter(h => !known.some(k => norm(k) === norm(h)));
      this.setState({
        upload: {
          type,
          file,
          headers,
          matched,
          ignored,
          rowCount: rows.length - 1,
          preview: rows.slice(1, 4),
          roles: [],
          levels: [],
          pdl: false
        }
      });
    });
    _defineProperty(this, "toggleUploadPick", (field, key) => () => {
      this.setState(st => {
        if (!st.upload) return null;
        const cur = st.upload[field] || [];
        const next = cur.indexOf(key) > -1 ? cur.filter(k => k !== key) : cur.concat(key);
        return {
          upload: {
            ...st.upload,
            [field]: next
          }
        };
      });
    });
    _defineProperty(this, "runUpload", async () => {
      const {
        upload
      } = this.state;
      if (!upload) return;
      this.setState({
        uploading: true
      });
      try {
        // The controller requires the PART's content type to be text/csv.
        const csvFile = new File([upload.file], upload.file.name, {
          type: "text/csv"
        });
        const fd = new FormData();
        fd.append("file", csvFile);
        if (upload.type === "company") {
          if (upload.roles.length) fd.append("roles", upload.roles.join(","));
          if (upload.levels.length) fd.append("levels", upload.levels.join(","));
          if (upload.pdl) fd.append("useAllProviders", "true");
        }
        const base = endpoints.audience && endpoints.audience.root || "";
        const res = await fetch(`${base}${PRODUCTS[upload.type].api}`, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + (AuthBase.idToken || AuthBase.accessToken || ""),
            "x-app-id": (account.get() || {}).tenantId || ""
          },
          body: fd
        });
        if (!res.ok) throw {
          message: `Upload failed (HTTP ${res.status}).`
        };
        if (this.toast) this.toast.show(`Enrichment started: ${upload.rowCount.toLocaleString()} rows queued.`);
        this.setState({
          upload: null,
          uploading: false
        });
        setTimeout(() => this.load(), 2500);
      } catch (err) {
        this.setState({
          uploading: false
        });
        if (this.toast) this.toast.show(err.message || "Upload failed.");
      }
    });
    // ── results ──
    _defineProperty(this, "openResults", async job => {
      this.setState({
        viewing: {
          job,
          rows: null
        },
        selected: {},
        addDlg: false,
        q: "",
        has: "all"
      });
      try {
        const data = await svc(`${PRODUCTS[job.type].api}/found/${String(job.id)}`).getAsync();
        if (this._alive) this.setState(st => st.viewing && String(st.viewing.job.id) === String(job.id) ? {
          viewing: {
            job,
            rows: Array.isArray(data) ? data : []
          }
        } : null);
      } catch (e) {
        if (this._alive) this.setState({
          viewing: {
            job,
            rows: []
          }
        });
      }
    });
    _defineProperty(this, "toggleRow", i => () => this.setState(st => {
      const selected = {
        ...st.selected
      };
      if (selected[i]) delete selected[i];else selected[i] = true;
      return {
        selected
      };
    }));
    // Header box: selects (or clears) every row that passes the current search and filter
    // and has an email or phone. Selections made under another filter are kept.
    _defineProperty(this, "toggleAll", visible => () => this.setState(st => {
      const usable = visible.filter(({
        r
      }) => splitBlob(r.emailAddress).length || splitBlob(r.phoneNumber).length).map(({
        i
      }) => i);
      const allOn = usable.length > 0 && usable.every(i => st.selected[i]);
      const selected = {
        ...st.selected
      };
      usable.forEach(i => {
        if (allOn) delete selected[i];else selected[i] = true;
      });
      return {
        selected
      };
    }));
    _defineProperty(this, "copyEmails", () => {
      const rows = this.visibleRows().map(({
        r
      }) => r);
      const seen = {};
      const emails = [];
      rows.forEach(r => splitBlob(r.emailAddress).forEach(e => {
        const k = e.toLowerCase();
        if (k.indexOf("@") > -1 && !seen[k]) {
          seen[k] = 1;
          emails.push(e);
        }
      }));
      if (!emails.length) {
        if (this.toast) this.toast.show("No emails on this job.");
        return;
      }
      const text = emails.join("\n");
      const done = () => {
        if (this.toast) this.toast.show(`${emails.length.toLocaleString()} emails copied.`);
      };
      const fallback = () => {
        try {
          const ta = document.createElement("textarea");
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          done();
        } catch (e) {
          if (this.toast) this.toast.show("Could not copy. Select the table and copy instead.");
        }
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, fallback);else fallback();
    });
    this.state = {
      jobs: null,
      // merged [{...job, type}]
      upload: null,
      // {type, file, headers, rowCount, matched, ignored, preview} — pre-flight modal
      uploading: false,
      viewing: null,
      // {job, rows, loading}
      selected: {},
      // results: row index -> true
      addDlg: false,
      // add-to-list dialog open
      q: "",
      // results search (company, name, title, email, phone)
      has: "all",
      // results filter: all | email | none
      error: ""
    };
    this._alive = true;
    this.fileRef = /*#__PURE__*/React.createRef();
    this.pendingType = null;
  }
  componentDidMount() {
    publishSearch("Data Enrichment");
    publishEvent("reset-search", {});
    this.toast = document.querySelector("paper-toast");
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._poll) clearTimeout(this._poll);
  }
  async load() {
    try {
      const [person, company] = await Promise.all([svc("/person/lookup").getAsync().catch(() => []), svc("/company/lookup").getAsync().catch(() => [])]);
      if (!this._alive) return;
      const jobs = [].concat((Array.isArray(person) ? person : []).map(j => ({
        ...j,
        type: "person"
      }))).concat((Array.isArray(company) ? company : []).map(j => ({
        ...j,
        type: "company"
      }))).sort((a, b) => String(b.createdDate).localeCompare(String(a.createdDate)));
      this.setState({
        jobs
      });
      if (jobs.some(j => !isTerminal(j.status) && !isStalled(j))) {
        if (this._poll) clearTimeout(this._poll);
        this._poll = setTimeout(() => this.load(), 10000);
      }
    } catch (e) {
      if (this._alive) this.setState({
        jobs: [],
        error: e.message || "Could not load jobs."
      });
    }
  }
  renderPreflight() {
    const {
      upload,
      uploading
    } = this.state;
    if (!upload) return null;
    const p = PRODUCTS[upload.type];
    return /*#__PURE__*/React.createElement("div", {
      className: "de-modal-backdrop",
      onClick: () => !uploading && this.setState({
        upload: null
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "de-modal",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("h3", {
      style: {
        margin: "0 0 6px"
      }
    }, p.title, ": check before running"), /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 10px"
      }
    }, /*#__PURE__*/React.createElement("b", null, upload.file.name), " \xB7 ", /*#__PURE__*/React.createElement("b", null, upload.rowCount.toLocaleString()), " data rows"), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 4px"
      }
    }, "Columns we'll use: ", upload.matched.length ? upload.matched.join(", ") : "none recognized"), upload.ignored.length > 0 && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 4px",
        color: "#a06a00"
      }
    }, "Ignored (not part of the ", upload.type, " lookup shape): ", upload.ignored.join(", ")), !upload.matched.length && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        color: "#dc2626"
      }
    }, "No columns we recognize. Expected any of: ", p.cols.join(", "), ". Rename your headers and re-upload."), upload.type === "company" && /*#__PURE__*/React.createElement("div", {
      className: "de-who"
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "10px 0 4px"
      }
    }, /*#__PURE__*/React.createElement("b", null, "Who to find"), " (leave empty for everyone on file)"), /*#__PURE__*/React.createElement("div", {
      className: "de-chips"
    }, ROLE_OPTIONS.map(o => /*#__PURE__*/React.createElement("button", {
      key: o.key,
      type: "button",
      className: "de-chip" + (upload.roles.indexOf(o.key) > -1 ? " on" : ""),
      onClick: this.toggleUploadPick("roles", o.key)
    }, o.label))), /*#__PURE__*/React.createElement("div", {
      className: "de-chips"
    }, LEVEL_OPTIONS.map(o => /*#__PURE__*/React.createElement("button", {
      key: o.key,
      type: "button",
      className: "de-chip" + (upload.levels.indexOf(o.key) > -1 ? " on" : ""),
      onClick: this.toggleUploadPick("levels", o.key)
    }, o.label))), /*#__PURE__*/React.createElement("p", {
      className: "wa-foot",
      style: {
        margin: "4px 0 0"
      }
    }, "Owners, presidents and VPs always pass when you pick a senior level, whatever their department."), /*#__PURE__*/React.createElement("label", {
      className: "de-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!upload.pdl,
      onChange: e => this.setState({
        upload: {
          ...upload,
          pdl: e.target.checked
        }
      })
    }), /*#__PURE__*/React.createElement("span", null, "Also search People Data Labs by website. It knows titles by role and finds people the business directory misses. Uses one credit per person found."))), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "8px 0 12px"
      }
    }, "Each row is looked up in verified data sources. Results show up in the jobs list, usually within a few minutes. Jobs run without charge for now."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "8px",
        justifyContent: "flex-end"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      disabled: uploading,
      onClick: () => this.setState({
        upload: null
      })
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: uploading || !upload.matched.length,
      onClick: this.runUpload
    }, uploading ? "Uploading…" : "Run enrichment"))));
  }

  // Rows that pass the search + filter, each tagged with its index in the full list
  // (selection and add-to-list key on that index).
  visibleRows() {
    const {
      viewing,
      q,
      has
    } = this.state;
    const rows = viewing && viewing.rows || [];
    const needle = q.trim().toLowerCase();
    const out = [];
    rows.forEach((r, i) => {
      const emails = splitBlob(r.emailAddress);
      if (has === "email" && !emails.length) return;
      if (has === "none" && emails.length) return;
      if (needle) {
        const hay = [r.company, r.companyProvider, r.firstName, r.lastName, r.title, r.emailAddress, r.phoneNumber, r.locality, r.region].filter(Boolean).join(" ").toLowerCase();
        if (hay.indexOf(needle) < 0) return;
      }
      out.push({
        r,
        i
      });
    });
    return out;
  }
  selectedRows() {
    const {
      viewing,
      selected
    } = this.state;
    const rows = viewing && viewing.rows || [];
    return rows.filter((r, i) => selected[i]);
  }
  renderResults() {
    const {
      viewing
    } = this.state;
    const {
      job,
      rows
    } = viewing;
    const isCompany = job.type === "company";
    const {
      selected,
      addDlg,
      q,
      has
    } = this.state;
    const visible = rows ? this.visibleRows() : [];
    const shown = visible.slice(0, 200);
    const emailCount = visible.reduce((n, {
      r
    }) => n + (splitBlob(r.emailAddress).length ? 1 : 0), 0);
    const nSel = Object.keys(selected).length;
    const usableVisible = visible.filter(({
      r
    }) => splitBlob(r.emailAddress).length || splitBlob(r.phoneNumber).length).map(({
      i
    }) => i);
    const allOn = usableVisible.length > 0 && usableVisible.every(i => selected[i]);
    const filtered = rows && visible.length !== rows.length;
    const dash = "-";
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageHeader, {
      title: `Results: ${job.name}`,
      crumbs: /*#__PURE__*/React.createElement("a", {
        href: "#",
        onClick: e => {
          e.preventDefault();
          this.setState({
            viewing: null
          });
        }
      }, "Data Enrichment"),
      subtitle: `${Number(job.found).toLocaleString()} found, ${Number(job.notFound).toLocaleString()} not found, of ${Number(job.size).toLocaleString()} rows`,
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, rows && rows.length > 0 && /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        disabled: !nSel,
        onClick: () => this.setState({
          addDlg: true
        })
      }, "Add to list", nSel ? ` (${nSel.toLocaleString()})` : ""), rows && rows.length > 0 && /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: this.copyEmails
      }, "Copy emails", emailCount ? ` (${emailCount.toLocaleString()})` : ""), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.setState({
          viewing: null
        })
      }, "Back to jobs"))
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Found rows",
      hint: !rows ? "" : filtered ? `${visible.length.toLocaleString()} of ${rows.length.toLocaleString()}${visible.length > 200 ? ", first 200 shown" : ""}` : rows.length > 200 ? `first 200 of ${rows.length.toLocaleString()}` : ""
    }, rows && rows.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar de-toolbar"
    }, /*#__PURE__*/React.createElement(Search, {
      value: q,
      onChange: v => this.setState({
        q: v
      }),
      placeholder: "Search company, name, title, email or phone"
    }), /*#__PURE__*/React.createElement(Filters, null, /*#__PURE__*/React.createElement(Segmented, {
      options: [["all", "Everyone"], ["email", "With email"], ["none", "No email"]],
      value: has,
      onChange: v => this.setState({
        has: v
      })
    }))), !rows ? /*#__PURE__*/React.createElement(Pending, null) : !rows.length ? /*#__PURE__*/React.createElement(Empty, null, "No found rows on this job.") : !visible.length ? /*#__PURE__*/React.createElement(Empty, null, "Nobody matches that search.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      className: "de-sel"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: allOn,
      disabled: !usableVisible.length,
      onChange: this.toggleAll(visible),
      title: "Select everyone shown who has an email or phone"
    })), isCompany && /*#__PURE__*/React.createElement("th", null, "Company"), /*#__PURE__*/React.createElement("th", null, "Name"), isCompany && /*#__PURE__*/React.createElement("th", null, "Title"), /*#__PURE__*/React.createElement("th", null, "Email"), /*#__PURE__*/React.createElement("th", null, "Phone"), /*#__PURE__*/React.createElement("th", null, "Address"))), /*#__PURE__*/React.createElement("tbody", null, shown.map(({
      r,
      i
    }) => {
      const emails = splitBlob(r.emailAddress);
      const phones = splitBlob(r.phoneNumber);
      const extraAddr = splitBlob(r.addresses);
      const addr = [r.streetAddress, r.locality, r.region].filter(Boolean).join(", ");
      const usable = emails.length > 0 || phones.length > 0;
      return /*#__PURE__*/React.createElement("tr", {
        key: i,
        className: selected[i] ? "de-on" : ""
      }, /*#__PURE__*/React.createElement("td", {
        className: "de-sel"
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: !!selected[i],
        disabled: !usable,
        onChange: this.toggleRow(i),
        title: usable ? "" : "No email or phone"
      })), isCompany && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, r.company || dash), r.companyProvider && r.companyProvider !== r.company ? /*#__PURE__*/React.createElement("div", {
        className: "wa-foot",
        style: {
          margin: 0
        }
      }, r.companyProvider) : null), /*#__PURE__*/React.createElement("td", {
        style: {
          textTransform: "capitalize"
        }
      }, [r.firstName, r.lastName].filter(Boolean).join(" ").toLowerCase() || dash), isCompany && /*#__PURE__*/React.createElement("td", null, r.title || dash), /*#__PURE__*/React.createElement("td", null, emails[0] || dash, emails.length > 1 ? ` +${emails.length - 1}` : ""), /*#__PURE__*/React.createElement("td", null, phones[0] || dash, phones.length > 1 ? ` +${phones.length - 1}` : ""), /*#__PURE__*/React.createElement("td", null, addr || extraAddr[0] || dash, extraAddr.length > 1 ? ` (+${extraAddr.length - 1} known)` : ""));
    })))), /*#__PURE__*/React.createElement("div", {
      className: "de-apply"
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      disabled: !nSel,
      onClick: () => this.setState({
        addDlg: true
      })
    }, "Add ", nSel ? nSel.toLocaleString() + " " : "", "to list"), /*#__PURE__*/React.createElement("span", {
      className: "wa-foot"
    }, nSel ? `${nSel.toLocaleString()} selected.` : "Tick the people you want, or the box in the header for everyone shown who has an email or phone.", " Selected people become contacts, with company and title, and members of a list you pick or create.", visible.length > 200 ? " The header box covers every matching row, not only the 200 shown." : "")), addDlg && /*#__PURE__*/React.createElement(AddToListDialog, {
      rows: this.selectedRows(),
      jobId: String(job.id),
      onClose: () => this.setState({
        addDlg: false
      }),
      onDone: () => this.setState({
        addDlg: false,
        selected: {}
      })
    }))));
  }
  render() {
    const {
      jobs,
      viewing,
      error
    } = this.state;
    const dash = "-";
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view de-view"
    }, /*#__PURE__*/React.createElement("input", {
      ref: this.fileRef,
      type: "file",
      accept: ".csv",
      style: {
        display: "none"
      },
      onChange: this.onFile
    }), this.renderPreflight(), viewing ? this.renderResults() : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageHeader, {
      title: "Data Enrichment",
      subtitle: "Fill the gaps in any contact or company list. Upload a CSV and we add details from verified data sources."
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, Object.keys(PRODUCTS).map(key => {
      const p = PRODUCTS[key];
      return /*#__PURE__*/React.createElement(Card, {
        key: key,
        title: p.title,
        span: 6,
        cls: "de-product"
      }, /*#__PURE__*/React.createElement("p", {
        className: "de-line"
      }, p.line), /*#__PURE__*/React.createElement("p", {
        className: "wa-foot"
      }, "CSV columns (any of these): ", p.cols.join(", ")), /*#__PURE__*/React.createElement("div", {
        className: "de-actions"
      }, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        onClick: this.pickFile(key)
      }, "Upload list")));
    }), /*#__PURE__*/React.createElement(Card, {
      title: "Jobs",
      hint: jobs && jobs.length ? `${jobs.length.toLocaleString()} total` : ""
    }, error && /*#__PURE__*/React.createElement("p", {
      className: "wa-foot de-error"
    }, error), !jobs ? /*#__PURE__*/React.createElement(Pending, null) : !jobs.length ? /*#__PURE__*/React.createElement(Empty, null, "No enrichment jobs yet. Upload a list above to start one.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Job"), /*#__PURE__*/React.createElement("th", null, "Type"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Rows"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Found"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Not found"), /*#__PURE__*/React.createElement("th", null, "Started"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }))), /*#__PURE__*/React.createElement("tbody", null, jobs.map(job => {
      const st = statusPill(job);
      const done = String(job.status).toLowerCase() === "completed";
      return /*#__PURE__*/React.createElement("tr", {
        key: `${job.type}-${String(job.id)}`,
        className: done ? "clickable" : "",
        onClick: () => done && this.openResults(job)
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, job.name)), /*#__PURE__*/React.createElement("td", null, job.type === "company" ? "Company" : "Person"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: `simf-pill ${st.cls}`
      }, st.label)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, Number(job.size) ? Number(job.size).toLocaleString() : dash), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, done ? Number(job.found).toLocaleString() : dash), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, done ? Number(job.notFound).toLocaleString() : dash), /*#__PURE__*/React.createElement("td", null, job.createdDate ? String(job.createdDate).slice(0, 10) : dash), /*#__PURE__*/React.createElement("td", {
        className: "n",
        onClick: e => e.stopPropagation()
      }, done && /*#__PURE__*/React.createElement("a", {
        href: "#",
        className: "wa-rowlink",
        onClick: e => {
          e.preventDefault();
          this.openResults(job);
        }
      }, "Review results")));
    })))), jobs && jobs.some(j => !isTerminal(j.status) && !isStalled(j)) && /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Running jobs refresh on their own. Larger lists take longer. You can leave this page and the results will be here."), jobs && jobs.some(isStalled) && /*#__PURE__*/React.createElement("p", {
      className: "wa-foot de-warn"
    }, "Stalled means it was queued over a day ago and never finished. Contact support to run it again.")))));
  }
}
const Routable = withRouter(MainComponent);
export class DataEnrichmentViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}
export default {
  DataEnrichmentViewComponent
};